import {Injectable} from '@angular/core';
import {CalculationData} from '../../models/calculation-data.model';
import {ProcessRequest} from '../../models/process-request.model';
import {RequestMetricResult} from '../../../shared/components/models/metrics/request-metric-result.enum';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {MetricsService, RequestMetricName} from '../../../shared/services/metrics.service';
import {RequestHeader} from '../../../shared/models/request-offer/request-header.model';
import {BusinessParamName, MessageType} from '../../../shared/models/request-offer/request-offer.enum';
import {v4 as uuid} from 'uuid';
import * as _ from 'lodash';
import {ConfigurationService} from './configuration/configuration.service';
import {RestApiService} from '../../../shared/services/rest-api.service';
import {ProcessService} from '../process.service';
import {LanguageService} from '../../../shared/services/language.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {DialogKey} from '../../../shared/components/dialogs/dialogs-configuration';
import {DialogService} from '../dialog.service';
import {CalculationService} from './calculation.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {ApplicationEvent, ApplicationEventsService} from '../../../shared/services/application-events.service';
import {VehicleModelStateService} from './section-services/vehicle-model/vehicle-model-state.service';
import * as moment from 'moment';
import {ApplicationContextService} from './configuration/application-context.service';
import {ApiParamsService} from '../api-params.service';
import {ApiRequestKey} from '../../../shared/enums/api-request-key.enum';

@Injectable({
  providedIn: 'root'
})
export class CalculationPolicyService {

  private readonly CLASSNAME = 'CalculationPolicyService';
  private readonly API_REQUEST_KEY: ApiRequestKey = ApiRequestKey.POST_CALCULATION;
  private _calculationId: string;
  private context: PostCalculationContext;

  constructor(private configuration: ConfigurationService,
              private calculation: CalculationService,
              private metricsService: MetricsService,
              private languageService: LanguageService,
              private appStateService: ApplicationStateService,
              private processService: ProcessService,
              private eventsService: ApplicationEventsService,
              private dialogService: DialogService,
              private vehicleModelStateService: VehicleModelStateService,
              private contextService: ApplicationContextService,
              private api: RestApiService,
              private apiParamsService: ApiParamsService) {
  }

  get calculationId(): string {
    return this._calculationId;
  }

  get calculationData(): CalculationData {
    return this.calculation.calculationData;
  }

  get isPostCalculationContextDefined(): boolean {
    return this.context === PostCalculationContext.CALCULATION_LINK_EMAIL || this.context === PostCalculationContext.OFFER_LINK_EMAIL;
  }

  get postCalculationEndpoint(): string {
    return this.isPostCalculationContextDefined ? this.configuration.uiConfig.calculationOptionalEmailEndpoint : this.configuration.uiConfig.calculationEndpoint;
  }

  get methodNameForMetric(): string {
    switch (this.context) {
      case PostCalculationContext.CALCULATION_LINK_EMAIL:
        return 'sendCalculationLinkEmail';
      case PostCalculationContext.OFFER_LINK_EMAIL:
        return 'sendOfferLinkEmail';
      default:
        return 'postCalculation';
    }
  }

  postCalculation(_calculationData?: CalculationData, context?: PostCalculationContext, payload?: OptionalEmailPayload): Promise<void> {
    this.context = context;

    if (this.calculationData.contact && !!this.calculationData.contact.email) {
      this.appStateService.createAnalyticsUserId(this.calculationData.contact.email);
    }

    const calculationData = _calculationData ? _calculationData : _.cloneDeep(this.calculation.calculationData);

    let body: ProcessRequest = {
      header: this.buildRequestHeader(),
      body: {
        calculationData: this.processCalculationData(calculationData)
      }
    };

    if (isNotNullOrUndefined(payload)) {
      body = this.addOptionalEmailData(body, payload);
    }

    let metricName = this.prepareMetrics(body);

    if (!this.isPostCalculationContextDefined) {
      this.eventsService.sendEvent(ApplicationEvent.CALCULATE, this.buildPayloadForEvent());
    }

    return this.api.post(this.postCalculationEndpoint, body)
      .then(() => { // RESPONSE
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.SUCCESS
        });
        return Promise.resolve();
      })
      .catch(error => { // RESPONSE
        console.error(error);
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);

        this.handlePostCalculationError(error);

        return Promise.reject(error);
      });
  }

  private handlePostCalculationError(error: any): void {
    if (error && error.status === 403) {
      this.dialogService.open(DialogKey.CLIENT_BLACK_LISTED);
    } else {
      if (error.errorCode && error.errorCode.message) {
        this.openPostCalculationErrorDialog(error.errorCode.message);
      }
    }
  }

  private addOptionalEmailData(body: any, payload: OptionalEmailPayload): any {
    return {
      header: body.header,
      body: {
        ...body.body,
        ...payload
      }
    };
  }

  private buildPayloadForEvent(): any {
    return {
      calculationId: this.calculationId,
      registrationNumber: this.calculationData.vehicle.registrationNumber,
      brandName: this.vehicleModelStateService.vehicleModelState ? this.vehicleModelStateService.vehicleModelState.brandName : null,
      startDate: moment(this.calculationData.insuranceData.startInsuranceDate).format('YYYY-MM-DD'),
      productionYear: this.calculationData.vehicle.productionYear,
      firstName: this.calculationData.owner.firstName,
      lastName: this.calculationData.owner.lastName,
      pesel: this.calculationData.owner.pesel,
      postalCode: this.calculationData.owner.address.postalCode,
      phoneNumber: this.calculationData.contact.phoneNumber,
      email: this.calculationData.contact.email,
    }
  }

  private prepareMetrics(data: ProcessRequest): any {
    if (!this.isPostCalculationContextDefined) {
      this.metricsService.sendSimpleMetric(CustomMetricsName.CALCULATION_DATA, MetricsType.COLLECTED_DATA, JSON.stringify(data.body.calculationData));
    }

    let metricName = {
      className: this.CLASSNAME,
      methodName: this.methodNameForMetric
    };

    this.metricsService.sendRequestMetric(<RequestMetricName>{
      ...metricName,
      type: RequestMetricResult.SEND
    }, data);

    return metricName;
  }

  private buildRequestHeader(): RequestHeader {
    const requestId = uuid();

    if (!this.isPostCalculationContextDefined) {
      this._calculationId = requestId;
      this.appStateService.saveBusinessParameter(BusinessParamName.CALCULATION_ID, requestId);
    }

    return {
      languageCode: this.languageService.getDefaultLanguage().toUpperCase(),
      requestId: requestId,
      responseId: requestId,
      messageType: MessageType.OFFER,
      requestDate: new Date(),
      responseDate: new Date(),
      processContext: {
        processId: this.processService.processId,
        businessParameters: this.apiParamsService.createBusinessParams({
          requestKey: this.API_REQUEST_KEY,
          payload: {
            postCalculationContextDefined: this.isPostCalculationContextDefined,
            postCalculationContext: this.context
          }
        })
      },
      technicalParameters: this.apiParamsService.createTechnicalParams({
        requestKey: this.API_REQUEST_KEY
      })
    };
  }

  private openPostCalculationErrorDialog(message: string): void {
    this.dialogService.open(DialogKey.CALCULATION_ERROR, {
      mainText: `CALCULATION_ERRORS.${message}`
    });
  }

  private isCalculationEmailMissing(calculationData: CalculationData): boolean {
    return !isNotNullOrUndefined(calculationData.contact.email) || calculationData.contact.email === '';
  }

  private processCalculationData(calculationData: CalculationData): CalculationData {
    calculationData = this.mockBirthDatesIfRequired(calculationData);

    if (calculationData.contact.phoneNumber && calculationData.contact.phoneNumber.length <= 2) {
      calculationData.contact.phoneNumber = null;
    }

    if (this.isCalculationEmailMissing(calculationData) && !this.contextService.calculationEmailRequired && this.contextService.defaultCalculationEmail) {
      calculationData.contact.email = this.contextService.defaultCalculationEmail;
    }

    return calculationData;
  }

  private mockBirthDatesIfRequired(calculationData: CalculationData): CalculationData {
    const mockedBirthDate = '1922-01-01T00:00:00.000Z';

    if (!calculationData.owner.birthDate) {
      calculationData.owner.birthDate = new Date(mockedBirthDate);
      calculationData.insurer.birthDate = new Date(mockedBirthDate);
    }
    calculationData.owner.coOwners.forEach(coOwner => {
      if (!coOwner.birthDate) {
        coOwner.birthDate = mockedBirthDate;
      }
    });

    return calculationData;
  }
}

export enum PostCalculationContext {
  CALCULATION_LINK_EMAIL = 'calculationLinkEmail', // request send on second step
  OFFER_LINK_EMAIL = 'offerLinkEmail' // request send on third step
}

export interface OptionalEmailPayload {
  email: string;
  offerId?: string;
  coverages?: string[];
  detailCoverages?: OptionalEmailDetailCoverage[];
  policyAmount?: number;
  registrationNumber?: string;
  insuranceCompanyLogoLink?: string;
  loggedUserId?: string;
};

export interface OptionalEmailDetailCoverage {
  label: string;
  value: any;
}
